import type { ReactNode } from 'react'
import { AlertCircle, Loader2, Plug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type ExtensionPanePlaceholderVariant = 'loading' | 'error' | 'empty' | 'connect'

export interface ExtensionPanePlaceholderProps {
  variant: ExtensionPanePlaceholderVariant
  title: string
  description?: string
  action?: ReactNode
  testId?: string
  className?: string
}

const ICONS: Record<ExtensionPanePlaceholderVariant, ReactNode> = {
  loading: <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />,
  error: <AlertCircle className="h-8 w-8 text-red-500" />,
  empty: null,
  connect: <Plug className="h-8 w-8 text-muted-foreground" />,
}

/**
 * Fills the remaining list pane (below the filter bar) and centers loading /
 * error / quiet empty / connect. Teaching empties stay left-aligned elsewhere.
 */
export function ExtensionPanePlaceholder({
  variant,
  title,
  description,
  action,
  testId,
  className,
}: ExtensionPanePlaceholderProps) {
  return (
    <div
      className={cn(
        'absolute inset-0 flex items-center justify-center p-6',
        className,
      )}
      data-testid={testId}
    >
      <div className="max-w-xs text-center space-y-3">
        {ICONS[variant] ? <div className="flex justify-center">{ICONS[variant]}</div> : null}
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? (
          <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
        ) : null}
        {action}
      </div>
    </div>
  )
}

export function ExtensionPaneRetryButton({
  onClick,
  children = 'Try again',
}: {
  onClick: () => void
  children?: ReactNode
}) {
  return (
    <Button variant="outline" size="sm" onClick={onClick}>
      {children}
    </Button>
  )
}
